import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as CheckoutActions from 'app/actions/CheckoutActions';
import * as HoldActions from 'app/actions/HoldActions';
import * as BorrowingActions from 'app/actions/BorrowingActions';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { WORKFLOW_VIEWS } from 'app/constants/HoldsConstants';
import { selectCurrentAccount } from 'app/selectors/AuthSelector';
import {
  selectPickupBranches,
  selectActivePreferredBranches,
  selectCurrentLibrary
} from 'app/selectors/LibrarySelector';
import { selectActiveHoldsByMetadataId, selectHoldsTransactions } from 'app/selectors/HoldsSelector';
import { selectCheckoutsByMetadataId } from 'app/selectors/CheckoutSelector';
import { selectShelvesForCurrentUser } from 'app/selectors/ShelfSelector';
import accountShape from 'app/shapes/accountShape';
import {
  ItemLevelHoldsWorkflowView,
  PhysicalHoldsWorkflowView,
  SingleCancelHoldWorkflowView,
  DigitalCheckoutWorkflowView,
  DigitalHoldsWorkflowView,
  DigitalMissingFormatWorkflowView
} from '../shared/ItemTransactions';
import SinglePauseHoldWorkflowView from '../borrowing/holds/PauseHoldWorkflowView/components/SinglePauseHoldWorkflowView';

// Routing + per-view prop-adaptation as data: `component` is the view to render for a
// given `view` value, `getProps` maps the container's props to that view's props. This
// keeps the container's routing pure and testable without mounting the heavy view
// subtrees (each view's rendering is covered by its own spec). Physical holds is the
// default (was the switch's default branch).
export const WORKFLOW_VIEW_CONFIG = {
  [WORKFLOW_VIEWS.CANCEL_HOLD]: {
    component: SingleCancelHoldWorkflowView,
    getProps: props => ({
      messagingId: props.messagingId,
      workflowId: props.workflowId || props.bib.get('metadataId'),
      bib: props.bib,
      holdId: props.holdsId,
      cancelHold: props.holdActions.cancelHold,
      handleClose: props.closeWorkflow,
      isLoading: props.isLoading || props.holdsTransactions.getIn([props.holdsId, 'isCancelling'], false),
      accountId: props.accountId,
      shelves: props.shelves,
      fieldErrors: props.fieldErrors
    })
  },
  [WORKFLOW_VIEWS.ITEM_LEVEL_HOLD]: {
    component: ItemLevelHoldsWorkflowView,
    getProps: props => ({
      messagingId: props.messagingId,
      workflowId: props.workflowId || props.bib.get('metadataId'),
      bib: props.bib,
      branches: props.branches,
      preferredBranches: props.preferredBranches,
      currentAccount: props.currentAccount,
      dispatch: props.dispatch,
      handleClose: props.closeWorkflow,
      selectedBranch: props.selectedBranch,
      placeItemHold: props.holdActions.placeItemHold,
      fetchItemGroups: props.holdActions.getItemGroups,
      isLoading: props.isLoading,
      fieldErrors: props.fieldErrors,
      itemGroups: props.itemGroups,
      itemIdsOnHold: props.itemIdsOnHold,
      successfulItemIds: props.successfulItemIds,
      failedItemIds: props.failedItemIds,
      supportsBatchVolumeHolds: props.materialPolicy.get('supportsBatchVolumeHolds'),
      showVolumeSelectorCollection: props.showVolumeSelectorCollection,
      showVolumeSelectorStatus: props.showVolumeSelectorStatus,
      showVolumeSelectorDueDate: props.showVolumeSelectorDueDate,
      isFetchingItemGroups: props.isFetchingItemGroups,
      provider: props.provider
    })
  },
  [WORKFLOW_VIEWS.DIGITAL_MISSING_FORMAT]: {
    component: DigitalMissingFormatWorkflowView,
    getProps: props => ({
      messagingId: props.messagingId,
      workflowId: props.workflowId || props.bib.get('metadataId'),
      lockInFormat: props.checkoutActions.lockInFormat,
      formats: props.formats,
      bib: props.bib,
      checkout: props.checkout,
      provider: props.provider,
      accountId: props.accountId,
      isLoading: props.isLoading,
      handleClose: props.closeWorkflow,
      fetchDigitalFormats: props.checkoutActions.fetchDigitalFormats
    })
  },
  [WORKFLOW_VIEWS.DIGITAL_CHECKOUT]: {
    component: DigitalCheckoutWorkflowView,
    getProps: props => ({
      bib: props.bib,
      holdId: props.holdsId,
      workflowId: props.workflowId || props.bib.get('metadataId'),
      messagingId: props.messagingId,
      handleClose: props.closeWorkflow,
      accountId: props.accountId,
      isLoading: props.isLoading,
      itemPosition: props.itemPosition,
      itemPositionGrouped: props.itemPositionGrouped,
      totalCount: props.totalCount,
      holdActions: props.holdActions,
      borrowingActions: props.borrowingActions,
      digitalCheckout: props.checkoutActions.digitalCheckout,
      provider: props.provider,
      requireFormats: props.materialPolicy.get('requiresFormatDuringCheckout'),
      fieldErrors: props.fieldErrors,
      formats: props.formats,
      fetchDigitalFormats: props.checkoutActions.fetchDigitalFormats,
      quickStartUrl: props.materialPolicy.get('quickStartUrl'),
      showQuickStartLink: props.materialPolicy.get('showQuickstartLink'),
      reciprocalRank: props.reciprocalRank
    })
  },
  [WORKFLOW_VIEWS.DIGITAL_HOLD]: {
    component: DigitalHoldsWorkflowView,
    getProps: props => ({
      bib: props.bib,
      provider: props.provider,
      isLoading: props.isLoading,
      itemPosition: props.itemPosition,
      itemPositionGrouped: props.itemPositionGrouped,
      totalCount: props.totalCount,
      handleClose: props.closeWorkflow,
      formats: props.formats,
      showFormatDuringHold: props.materialPolicy.get('showFormatDuringHold'),
      requiresFormatDuringHold: props.materialPolicy.get('requiresFormatDuringHold'),
      quickStartUrl: props.materialPolicy.get('quickStartUrl'),
      showQuickStartLink: props.materialPolicy.get('showQuickstartLink'),
      accountId: props.accountId,
      placeHold: props.holdActions.placeHold,
      digitalNotificationEmail: props.digitalNotificationEmail,
      fetchDigitalFormats: props.checkoutActions.fetchDigitalFormats,
      workflowId: props.workflowId,
      messagingId: props.messagingId,
      reciprocalRank: props.reciprocalRank
    })
  },
  [WORKFLOW_VIEWS.PAUSE_HOLD]: {
    component: SinglePauseHoldWorkflowView,
    getProps: props => ({
      bib: props.bib,
      messagingId: props.messagingId || props.holdsId,
      workflowId: props.workflowId || props.holdsId,
      holdId: props.holdsId,
      initialSuspendStartDate: props.initialSuspendStartDate,
      initialSuspendEndDate: props.initialSuspendEndDate,
      enableStartDate: props.enableStartDate,
      enableEndDate: props.enableEndDate,
      pauseHolds: props.holdActions.pauseOrResumeHolds,
      handleClose: props.closeWorkflow,
      isLoading: props.isLoading,
      accountId: props.accountId,
      fieldErrors: props.fieldErrors
    })
  }
};

export const DEFAULT_WORKFLOW_VIEW_CONFIG = {
  component: PhysicalHoldsWorkflowView,
  getProps: props => ({
    bib: props.bib,
    branches: props.branches,
    itemPosition: props.itemPosition,
    itemPositionGrouped: props.itemPositionGrouped,
    totalCount: props.totalCount,
    preferredBranches: props.preferredBranches,
    handleClose: props.closeWorkflow,
    placeHold: props.holdActions.placeHold,
    currentAccount: props.currentAccount,
    accountId: props.accountId,
    singleBranch: props.singleBranch,
    currentLocale: props.currentLocale,
    requiresExpiryDate: props.requiresExpiryDate,
    holdInfoMessages: props.holdInfoMessages,
    isLoading: props.isLoading,
    fieldErrors: props.fieldErrors,
    workflowId: props.workflowId,
    messagingId: props.messagingId,
    reciprocalRank: props.reciprocalRank
  })
};

export const getWorkflowViewConfig = view =>
  Object.prototype.hasOwnProperty.call(WORKFLOW_VIEW_CONFIG, view)
    ? WORKFLOW_VIEW_CONFIG[view]
    : DEFAULT_WORKFLOW_VIEW_CONFIG;

export class ItemTransactionWorkflowContainer extends React.PureComponent {
  render() {
    const { component: View, getProps } = getWorkflowViewConfig(this.props.view);
    return <View {...getProps(this.props)} />;
  }
}

ItemTransactionWorkflowContainer.propTypes = {
  bib: ImmutablePropTypes.map,
  dispatch: PropTypes.func.isRequired,
  materialPolicy: ImmutablePropTypes.map.isRequired,
  holdsId: PropTypes.string,
  closeWorkflow: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  holdInfoMessages: ImmutablePropTypes.listOf(PropTypes.number).isRequired,
  currentAccount: accountShape.isRequired,
  accountId: PropTypes.number.isRequired,
  shelves: ImmutablePropTypes.map.isRequired,
  branches: ImmutablePropTypes.listOf(ImmutablePropTypes.map).isRequired,
  currentLocale: PropTypes.string,
  selectedBranch: PropTypes.string,
  preferredBranches: ImmutablePropTypes.list.isRequired,
  singleBranch: PropTypes.bool,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  borrowingActions: PropTypes.objectOf(PropTypes.func).isRequired,
  requiresExpiryDate: PropTypes.bool.isRequired,
  fieldErrors: PropTypes.objectOf(
    PropTypes.shape({
      classification: PropTypes.string,
      message: PropTypes.string
    })
  ),
  provider: PropTypes.string.isRequired,
  formats: ImmutablePropTypes.listOf(
    ImmutablePropTypes.contains({
      name: PropTypes.string,
      hasDownloadLink: PropTypes.bool
    })
  ),
  checkout: ImmutablePropTypes.map,
  digitalNotificationEmail: PropTypes.string,
  view: PropTypes.string,
  itemGroups: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      volume: PropTypes.string,
      collection: PropTypes.string.isRequired,
      copy: PropTypes.string,
      itemId: PropTypes.string.isRequired,
      callNumber: PropTypes.string
    })
  ),
  itemIdsOnHold: ImmutablePropTypes.listOf(PropTypes.string),
  failedItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  successfulItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  showVolumeSelectorCollection: PropTypes.bool,
  showVolumeSelectorStatus: PropTypes.bool,
  showVolumeSelectorDueDate: PropTypes.bool,
  isFetchingItemGroups: PropTypes.bool,
  messagingId: PropTypes.string,
  workflowId: PropTypes.string,
  reciprocalRank: PropTypes.number,
  holdsTransactions: ImmutablePropTypes.map,
  enableEndDate: PropTypes.bool,
  enableStartDate: PropTypes.bool,
  initialSuspendEndDate: PropTypes.string,
  initialSuspendStartDate: PropTypes.string
};

ItemTransactionWorkflowContainer.displayName = 'ItemTransactionWorkflowContainer';

const mapStateToProps = (state, ownProps) => {
  const account = selectCurrentAccount(state, ownProps) || Immutable.Map();
  const library = selectCurrentLibrary(state);
  const policy = state.getIn(['entities', 'bibs', ownProps.metadataId, 'policy'], Immutable.Map());
  const branches = selectPickupBranches(state, ownProps);
  const checkouts = selectCheckoutsByMetadataId(state).get(ownProps.metadataId, Immutable.List());

  const holds = selectActiveHoldsByMetadataId(state).get(ownProps.metadataId, Immutable.List());
  const holdsId = ownProps.holdsId || (holds.size === 1 ? holds.first().get('holdsId') : null);
  const initialSuspendStartDate = holds.size === 1 ? holds.first().get('suspendStartDate') : null;
  const initialSuspendEndDate = holds.size === 1 ? holds.first().get('suspendEndDate') : null;
  const bibs = selectBibEntities(state);
  return {
    bib: state.getIn(['entities', 'bibs', ownProps.metadataId, 'briefInfo']),
    enableStartDate: bibs.getIn([ownProps.metadataId, 'policy', 'materialPolicy', 'supportsSuspendHoldStartDateEdit']),
    enableEndDate: bibs.getIn([ownProps.metadataId, 'policy', 'materialPolicy', 'supportsSuspendHoldEndDateEdit']),
    itemGroups: state.getIn(['entities', 'bibs', ownProps.metadataId, 'itemGroups'], Immutable.List()),
    holdsId,
    initialSuspendStartDate,
    initialSuspendEndDate,
    itemIdsOnHold: holds.map(hold => hold.get('itemId')).toList(),
    checkout: checkouts.first(),
    formats: state.getIn(['entities', 'bibs', ownProps.metadataId, 'policy', 'formats'], null),
    branches,
    preferredBranches: selectActivePreferredBranches(state),
    currentAccount: account,
    provider: policy.get('provider'),
    materialPolicy: policy.get('materialPolicy', Immutable.Map()),
    accountId: account.get('id'),
    shelves: selectShelvesForCurrentUser(state),
    singleBranch: library.get('singleBranch'),
    selectedBranch: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'selectedBranch']),
    isLoading: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'isLoading']),
    currentLocale: state.getIn(['localization', 'currentLanguage'], 'en-US'),
    requiresExpiryDate: library.get('requiresHoldExpiryDate', false),
    holdInfoMessages: library.get('holdInfoMessages', Immutable.List()),
    availableForCheckout:
      state.getIn(['entities', 'bibs', ownProps.metadataId, 'availability', 'status'], false) === 'AVAILABLE',
    digitalNotificationEmail: account.get('digitalNotificationEmail'),
    failedItemIds: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'failedItemIds'], Immutable.List()),
    successfulItemIds: state.getIn(
      ['ui', 'itemTransactions', ownProps.metadataId, 'successfulItemIds'],
      Immutable.List()
    ),
    showVolumeSelectorCollection: library.get('showVolumeSelectorCollection'),
    showVolumeSelectorStatus: library.get('showVolumeSelectorStatus'),
    showVolumeSelectorDueDate: library.get('showVolumeSelectorDueDate'),
    isFetchingItemGroups: state.getIn(['ui', 'itemLevelHold', ownProps.metadataId, 'isFetching']),
    holdsTransactions: selectHoldsTransactions(state)
  };
};

const mapDispatchToProps = dispatch => ({
  holdActions: bindActionCreators(HoldActions, dispatch),
  checkoutActions: bindActionCreators(CheckoutActions, dispatch),
  borrowingActions: bindActionCreators(BorrowingActions, dispatch),
  dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemTransactionWorkflowContainer);
